import { store } from '../../store/store';
import type { RootState } from '../../store/store';
import { middlewareService } from '../middleware/middlewareService';

/**
 * Structured diagnostic logging for the visitor auth + document fetch flow.
 *
 * Purpose: give us a complete, queryable snapshot of client state at the
 * moment an auth-related or app failure happens, so production 401s / missing-token
 * bugs can be diagnosed from telemetry alone without guessing.
 *
 * Every diagnostic event carries:
 *   - reason: a short machine-readable tag (e.g. 'request_401',
 *     'interceptor_recovered', 'get_auth_headers_missing')
 *   - api: the failing URL / method / status
 *   - accessToken / prefillShortToken: presence, length, Bearer-prefix check
 *     (NEVER the JWT content — no PII leaked)
 *   - page: current URL, pathname, whether ?token= is in the URL
 *   - redux: presence/count of orgConfig, orgTemplateIds, visitorinfo, isPrefill
 *
 * Why not log the JWT content: JWT payload is base64-encoded visitor
 * identity data (preregistration ID, orgId). We log "present/absent",
 * length, and whether it has the `Bearer ` prefix — enough to tell
 * missing vs empty vs malformed without leaking identity.
 */

const SHORT_TOKEN_KEY = 'prefillShortToken';
const ACCESS_TOKEN_KEY = 'accessToken';
const ORGCONFIG_KEY = 'orgconfig';

export type AuthDiagnosticReason =
  /** A visitor-flow API call returned 401. Captured by the axios interceptor. */
  | 'request_401'
  /** A visitor-flow API call returned 403 (likely Redis short-token expired). */
  | 'request_403'
  /** A visitor-flow API call failed with Network Error (no response). */
  | 'request_network_error'
  /** touchlessService.getAuthHeaders was asked for headers but accessToken is empty/missing. */
  | 'get_auth_headers_missing'
  /** Interceptor successfully re-minted the JWT and retried the request. */
  | 'interceptor_recovered'
  /** Interceptor tried to re-mint but re-auth call itself failed. */
  | 'interceptor_reauth_failed'
  /** Interceptor could not recover (no short token available anywhere). */
  | 'interceptor_no_short_token'
  /** PreRegister.getToken successfully stored a fresh JWT + short token. */
  | 'session_bootstrapped'
  /** SignDocument.fetchDocumentMultiple's catch handler fired. */
  | 'sign_document_fetch_failed';

export type AuthDiagnosticType = 'error' | 'warn' | 'info';

export interface AuthDiagnosticInput {
  reason: AuthDiagnosticReason;
  type?: AuthDiagnosticType;
  apiUrl?: string;
  apiMethod?: string;
  errorStatus?: number;
  errorMessage?: string;
  errorCode?: string;
  recovered?: boolean;
  /** Free-form extra context for the specific call site. */
  extra?: Record<string, unknown>;
}

const safeTokenSnapshot = (key: string) => {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(key);
  } catch {
    return { accessible: false };
  }
  if (raw == null) return { accessible: true, present: false };
  return {
    accessible: true,
    present: true,
    length: raw.length,
    startsWithBearer: raw.startsWith('Bearer '),
    isLiteralUndefined:
      raw === 'undefined' ||
      raw === 'null' ||
      raw === 'Bearer undefined' ||
      raw === 'Bearer null',
    // 'Bearer ' prefix is safe to log; 3 more chars is the JWT header's
    // base64-encoded `{"alg"` which is non-sensitive.
    prefix: raw.substring(0, 10),
  };
};

const safePageSnapshot = () => {
  try {
    const url = new URL(window.location.href);
    return {
      pathname: url.pathname,
      hasTokenParam: url.searchParams.has('token'),
      hasTknParam: url.searchParams.has('tkn'),
      hasIdParam: url.searchParams.has('id'),
      search: url.search.length > 0,
      hash: url.hash.length > 0,
    };
  } catch {
    return { pathname: '(unavailable)', hasTokenParam: false };
  }
};

const safeReduxSnapshot = () => {
  let state: RootState;
  try {
    state = store.getState() as unknown as RootState;
  } catch {
    return { accessible: false };
  }
  const orgConfig = state.app?.config ?? null;
  const visitor = state.visitor ?? null;
  return {
    accessible: true,
    orgConfigPresent: !!orgConfig,
    orgConfigSiteNamePresent: !!orgConfig?.siteName,
    orgConfigOrgIdPresent: !!orgConfig?.orgId,
    orgConfigSiteIdPresent: !!orgConfig?.siteId,
    orgTemplateIdsCount: Array.isArray(visitor?.orgTemplateIds)
      ? visitor?.orgTemplateIds?.length ?? 0
      : 0,
    visitorInfoPresent: !!visitor?.visitorinfo,
    visitorTypeIdPresent: !!visitor?.visitorTypeId,
    preregisterIdPresent: !!visitor?.preregisterId,
    isPrefill: !!visitor?.isPrefill,
    preFillNew: visitor?.preFillNew ?? null,
  };
};

const safeOrgconfigSessionSnapshot = () => {
  try {
    const raw = sessionStorage.getItem(ORGCONFIG_KEY);
    if (!raw) return { present: false };
    const parsed = JSON.parse(raw);
    return {
      present: true,
      length: raw.length,
      siteNamePresent: !!parsed?.siteName,
      orgIdPresent: !!parsed?.orgId,
      siteIdPresent: !!parsed?.siteId,
    };
  } catch {
    return { present: true, parseError: true };
  }
};

/**
 * Emit one structured diagnostic event to Middleware RUM + console.
 *
 * This is a fire-and-forget — never throws, never blocks the caller. Call
 * sites can be wrapped in try/catch but aren't required to.
 *
 * @param input - Diagnostic data
 * @param input.type - Log severity: 'error' (logError), 'warn' (logWarn), or 'info' (logInfo)
 *   Default: 'error' for backwards compatibility
 */
export const logAuthDiagnostic = (input: AuthDiagnosticInput): void => {
  try {
    const context = {
      // Prefix all keys so queries like `reason:request_401` are unambiguous
      reason: input.reason,
      api: {
        url: input.apiUrl,
        method: input.apiMethod,
        status: input.errorStatus,
        message: input.errorMessage,
        code: input.errorCode,
      },
      recovered: input.recovered ?? false,
      accessToken: safeTokenSnapshot(ACCESS_TOKEN_KEY),
      prefillShortToken: safeTokenSnapshot(SHORT_TOKEN_KEY),
      orgconfigSession: safeOrgconfigSessionSnapshot(),
      page: safePageSnapshot(),
      redux: safeReduxSnapshot(),
      timeMs: Date.now(),
      ...(input.extra ?? {}),
    };

    // Local console for dev + in case Middleware is down
    // eslint-disable-next-line no-console
    // console.warn(`[app-diagnostic] ${input.reason}`, context);

    // Ship to Middleware RUM based on severity type
    const diagnosticType = input.type ?? 'error';
    if (diagnosticType === 'warn') {
      middlewareService.logWarn(`app_diagnostic:${input.reason}`, context);
    } else if (diagnosticType === 'info') {
      middlewareService.logInfo(`app_diagnostic:${input.reason}`, context);
    } else {
      middlewareService.logError(`app_diagnostic:${input.reason}`, context);
    }
  } catch (e) {
    // Absolute last-resort — never let diagnostics break the app.
    // eslint-disable-next-line no-console
    console.error('[app-diagnostic] self-failure', e);
  }
};
